import Singleton from '../common/Singleton';
import jsts from '../algorithm/jsts';
import Vector from '../math/Vector';
import Matrix from '../math/Matrix';
import MercatorTransform from '../transform/MercatorTransform';

/**
 * geoJson格式几何的各种计算的辅助类
 */
export default class GeometryAlgorithm extends Singleton {
    /**
     * 初始化构造函数.
     * @returns {undefined}
     */
    constructor(options) {
        super({
            precious: 7,
            ...options
        });

        this.precisionModel = new jsts.geom.PrecisionModel(1e12);
        this.factory = new jsts.geom.GeometryFactory(this.precisionModel);
        this.reader = new jsts.io.GeoJSONReader(this.factory);
        this.writer = new jsts.io.GeoJSONWriter(this.factory);
        this.wktReader = new jsts.io.WKTReader(this.factory);
        this.wktWriter = new jsts.io.WKTWriter(this.factory);
        this.mercatorTransform = new MercatorTransform();
        this.earthRadius = 6378.137;

        /**
         * 几何精度，小数点后的尾数
         * @type {Number}
         */
        this.precious = this.options.precious;
    }

    /**
     * 角度转为弧度
     * @param {Number} angle - 角度
     * @returns {Number} - 返回弧度
     */
    rad(angle) {
        return angle * Math.PI / 180.0;
    }

    /**
     * 计算两个经纬度坐标之间的球面距离
     * @param {Array} p1 - 经纬度坐标数组[x1, y1]
     * @param {Array} p2 - 经纬度坐标数组[x2, y2]
     * @returns {Number} - 返回球面距离,单位米
     */
    _sphericalDistance(p1, p2) {
        const radLat1 = this.rad(p1[1]);
        const radLat2 = this.rad(p2[1]);
        const a = radLat1 - radLat2;
        const b = this.rad(p1[0]) - this.rad(p2[0]);
        let s = 2 * Math.asin(Math.sqrt(Math.pow(Math.sin(a / 2), 2) +
            Math.cos(radLat1) * Math.cos(radLat2) * Math.pow(Math.sin(b / 2), 2)));
        s = s * this.earthRadius * 1000;

        return s;
    }

    /**
     * 计算两个几何之间的距离
     * @param {Object} geometry1 - 几何1
     * @param {Object} geometry2 - 几何1
     * @returns {Number} - 返回距离单位随传入几何的单位
     */
    distance(geometry1, geometry2) {
        const jstsGeometry1 = this.reader.read(geometry1);
        const jstsGeometry2 = this.reader.read(geometry2);
        return jstsGeometry1.distance(jstsGeometry2);
    }

    /**
     * 计算两个点之间的球面距离,单位为米.
     * @param {Object} point1 - geosjson格式的经纬点
     * @param {Object} point2 - geosjson格式的经纬点
     * @returns {number} - 返回距离
     */
    sphericalDistance(point1, point2) {
        return this._sphericalDistance(point1.coordinates, point2.coordinates);
    }

    /**
     * 计算两个几何之间最近的两个点以及距离
     * @param {Object} geometry1 - geosjson格式的经纬点
     * @param {Object} geometry2 - geosjson格式的经纬点
     * @returns {Object} Object 距离、G1与G2的垂直线坐标
     */
    nearestPoints(geometry1, geometry2) {
        const jstsGeometry1 = this.reader.read(geometry1);
        const jstsGeometry2 = this.reader.read(geometry2);
        const pair = jsts.operation.distance.DistanceOp.nearestPoints(jstsGeometry1, jstsGeometry2);
        const res = {
            distance: pair[0].distance(pair[1]),
            point1: this.writer.write(this.factory.createPoint(pair[0])),
            point2: this.writer.write(this.factory.createPoint(pair[1])),
        };
        return res;
    }

    /**
     * 返回以center为中心，扩展N米的一个矩形
     * @param {lng, lat} center
     * @param  {number} hmeters 单位米 横向延展
     * @param  {number} vmeters 单位米 纵向延展
     * @return {Array}
     */
    createBound(center, hmeters, vmeters) {
        const coord = this.mercatorTransform.geographic2Mercator(center[0], center[1]);
        const bounds = [
            [coord[0] - hmeters, coord[1] - vmeters], // 左下
            [coord[0] + hmeters, coord[1] - vmeters], // 右下
            [coord[0] + hmeters, coord[1] + vmeters], // 右上
            [coord[0] - hmeters, coord[1] + vmeters] // 左上
        ];

        return bounds.map(c => this.mercatorTransform.mercator2Geographic(c[0], c[1]));
    }

    /**
     * 判断两个几何是否包含
     * @params { GeoJson } geometry1 几何1
     * @params { GeoJson } geometry2 几何2
     * @return { Boolean }
     */
    contains(geometry1, geometry2) {
        const jstsGeometry1 = this.reader.read(geometry1);
        const jstsGeometry2 = this.reader.read(geometry2);
        return jstsGeometry1.contains(jstsGeometry2);
    }

    /**
     * 判断两个几何是否相交
     * @params { GeoJson } geometry1 几何1
     * @params { GeoJson } geometry2 几何2
     * @return { Boolean }
     */
    intersects(geometry1, geometry2) {
        const jstsGeometry1 = this.reader.read(geometry1);
        const jstsGeometry2 = this.reader.read(geometry2);
        return jstsGeometry1.intersects(jstsGeometry2);
    }

    /**
     * 计算两个几何的交集
     * @params { GeoJson } geometry1 几何1
     * @params { GeoJson } geometry2 几何2
     * @return { GeoJson } 交集几何
     */
    intersection(geometry1, geometry2) {
        const jstsGeometry1 = this.reader.read(geometry1);
        const jstsGeometry2 = this.reader.read(geometry2);
        return this.writer.write(jstsGeometry1.intersection(jstsGeometry2));
    }

    isWithinDistance(geometry1, geometry2, distance) {
        const jstsGeometry1 = this.reader.read(geometry1);
        const jstsGeometry2 = this.reader.read(geometry2);
        return jstsGeometry1.isWithinDistance(jstsGeometry2, distance);
    }

    intersectsAndContains(geometry1, geometry2) {
        const jstsGeometry1 = this.reader.read(geometry1);
        const jstsGeometry2 = this.reader.read(geometry2);
        // 采用三元表达式,尽量减少计算相交的次数
        return jstsGeometry1.contains(jstsGeometry2) ? true : jstsGeometry1.intersects(jstsGeometry2);
    }

    bbox(geometry) {
        const jstsGeometry = this.reader.read(geometry);
        const envelope = jstsGeometry.getEnvelopeInternal();

        return {
            minX: envelope.getMinX(),
            minY: envelope.getMinY(),
            maxX: envelope.getMaxX(),
            maxY: envelope.getMaxY(),
        };
    }

    getEnvelope(geometry) {
        const jstsGeometry = this.reader.read(geometry);
        return jstsGeometry.getEnvelopeInternal();
    }

    bboxToPolygon(bbox) {
        const geojson = {
            type: 'Polygon',
            coordinates: [
                [
                    [bbox.minX, bbox.minY],
                    [bbox.maxX, bbox.minY],
                    [bbox.maxX, bbox.maxY],
                    [bbox.minX, bbox.maxY],
                    [bbox.minX, bbox.minY],
                ],
            ],
        };
        return geojson;
    }

    /**
     * 计算几何质心
     * @param geometry
     * @return {{coordinates: [*, *], type: string}}
     */
    centroid(geometry) {
        const jstsGeometry = this.reader.read(geometry);
        const coordinate = jstsGeometry.getCentroid().getCoordinate();
        const point = {
            type: 'Point',
            coordinates: [coordinate.x, coordinate.y],
        };
        return point;
    }

    buffer(geometry, distance) {
        const jstsGeometry = this.reader.read(geometry);
        const parameters = new jsts.operation.buffer.BufferParameters();
        parameters.setEndCapStyle(jsts.operation.buffer.BufferParameters.CAP_ROUND);
        parameters.setJoinStyle(jsts.operation.buffer.BufferParameters.JOIN_ROUND);
        // parameters.setSingleSided(true);
        const bufferOp = new jsts.operation.buffer.BufferOp(jstsGeometry, parameters);
        const buffer = bufferOp.getResultGeometry(distance);
        return this.writer.write(buffer);
    }

    convexHull(geometry) {
        const jstsGeometry = this.reader.read(geometry);
        const convexHull = jstsGeometry.convexHull();
        return this.writer.write(convexHull);
    }

    /**
     * 计算点几何在线上最近的两个形状点信息
     * @param point
     * @param lineString
     * @return {{previousPoint, distance: *, nextPoint, nextIndex: *, point: *, previousIndex, nearestIndex: *}}
     */
    nearestLocations(point, lineString) {
        const jstsPoint = this.reader.read(point);
        const jstsLineString = this.reader.read(lineString);
        const distanceOp = new jsts.operation.distance.DistanceOp(jstsPoint, jstsLineString);
        const pair = distanceOp.nearestLocations();
        const p0 = this.factory.createPoint(pair[0].getCoordinate());
        const p1 = this.factory.createPoint(pair[1].getCoordinate());
        const segmentIndex = pair[1].getSegmentIndex();
        const previousPoint = jstsLineString.getPointN(segmentIndex);
        const nextPoint = jstsLineString.getPointN(segmentIndex + 1);
        const d0 = p0.distance(previousPoint);
        const d1 = p0.distance(nextPoint);
        const res = {
            distance: p0.distance(p1),
            point: this.writer.write(p1),
            previousIndex: segmentIndex,
            nextIndex: segmentIndex + 1,
            previousPoint: this.writer.write(previousPoint),
            nextPoint: this.writer.write(nextPoint),
            nearestIndex: d0 <= d1 ? segmentIndex : segmentIndex + 1
        };
        return res;
    }

    /**
     * WKT转换为GeoJson
     * @param wkt
     * @return {null|*}
     */
    wktToGeojson(wkt) {
        try {
            const jstsGeometry = this.wktReader.read(wkt);
            return this.writer.write(jstsGeometry);
        } catch (e) {
            return null;
        }
    }

    /**
     * GeoJson转换为WKT
     * @param geojson
     * @return {null|*}
     */
    geoJsonToWkt(geojson) {
        try {
            const jstsGeometry = this.reader.read(geojson);
            return this.wktWriter.write(jstsGeometry);
        } catch (e) {
            return null;
        }
    }

    /**
     * 获取长度信息 以米为单位
     * @param geometry
     * @return {number}
     */
    getLength(geometry) {
        if (geometry.coordinates.length < 2) {
            return 0;
        }

        const jstsGeometry = this.reader.read(geometry);
        return jstsGeometry.getLength();
    }

    /**
     * 计算LineString长度,单位为米
     * @param lineString geosjson格式的经纬线
     * @returns {number}
     */
    sphericalLength(lineString) {
        let totalLength = 0;
        for (let i = 0; i < lineString.coordinates.length - 1; ++i) {
            const length = this._sphericalDistance(
                lineString.coordinates[i],
                lineString.coordinates[i + 1]
            );
            totalLength += length;
        }

        return totalLength;
    }

    isClosed(lineString) {
        const length = lineString.coordinates.length;
        if (length < 3) {
            return false;
        }
        const jstsGeometry = this.reader.read(lineString);
        return jstsGeometry.isClosed();
    }

    close(lineString) {
        const length = lineString.coordinates.length;
        if (length < 3) {
            throw new Error('LineString至少拥有三个坐标点才能闭合!');
        }
        lineString.coordinates[length] = lineString.coordinates[0];
    }

    /**
     * 计算给定角度之间的弧几何
     * @param x 圆心x
     * @param y 圆心y
     * @param radius 半径
     * @param startAngle 起始角度,单位度
     * @param endAngle 终止角度,单位度
     * @param distance 形状点之间距离
     * @returns {{type: string, coordinates: Array}}
     */
    arc(x, y, radius, startAngle, endAngle, distance) {
        if (endAngle < startAngle) {
            throw new Error('endAngle必须大于startAngle');
        }
        const perimeter = 2 * Math.PI * radius;
        const count = Math.floor(perimeter / distance);
        const remainderLength = perimeter % distance;
        const remainderAngle = 180 * remainderLength / (Math.PI * radius);
        const angleStep = (360 - remainderAngle) / count;

        let vector = new Vector(0, -1);
        vector = vector.multiNumber(radius);
        const matrix = new Matrix();
        const geometry = {
            type: 'LineString',
            coordinates: [],
        };

        let tmpVector = null;
        let tmpMatrix = null;
        for (let i = startAngle; i < endAngle;) {
            tmpMatrix = matrix.makeRotate(i);
            tmpVector = vector.crossMatrix(tmpMatrix);
            geometry.coordinates.push([x + tmpVector.x, y + tmpVector.y]);
            i += angleStep;
        }
        tmpMatrix = matrix.makeRotate(endAngle);
        tmpVector = vector.crossMatrix(tmpMatrix);
        geometry.coordinates.push([x + tmpVector.x, y + tmpVector.y]);

        return geometry;
    }

    equals(geometry1, geometry2) {
        const jstsGeometry1 = this.reader.read(geometry1);
        const jstsGeometry2 = this.reader.read(geometry2);
        return jstsGeometry1.equals(jstsGeometry2);
    }

    /**
     * 计算球面多边形的面积,不转换坐标,不考虑带洞的多边形
     * @param polygon polygon格式的经纬点
     * @returns {number}
     */
    getArea(polygon) {
        if (polygon.coordinates.length === 0) {
            return 0;
        }
        if (polygon.coordinates[0].length < 3) {
            return 0;
        }

        const jstsGeometry1 = this.reader.read(polygon);
        return jstsGeometry1.getArea();
    }

    /**
     * 判断几何是否是简单几何
     * 判断规则：一个几何图形没有自切点、自交点或其他异常点，那么它就是简单的
     * @param geometry
     * @return {bool} true or false
     */
    isSimple(geometry) {
        const jstsGeometry = this.reader.read(geometry);
        return jstsGeometry.isSimple();
    }

    isValidGeometry(geometry) {
        try {
            const jstsGeometry = this.reader.read(geometry);
            return true;
        } catch (err) {
            return false;
        }
    }

    _extractMultiGeometry(multiGeometry) {
        const geometries = [];
        const type = multiGeometry.type.substr(5);
        for (let i = 0; i < multiGeometry.coordinates.length; ++i) {
            geometries.push({
                type: type,
                coordinates: multiGeometry.coordinates[i]
            });
        }

        return geometries;
    }

    getSimpleGeometries(geometry, type) {
        if (!geometry) {
            return [];
        }
        const oType = geometry.type;
        const geometries = [];
        switch (oType) {
            case 'MultiPoint':
            case 'MultiLineString':
            case 'MultiPolygon':
                geometries.push(...this._extractMultiGeometry(geometry));
                break;
            case 'GeometryCollection':
                geometry.geometries.forEach(geom => {
                    geometries.push(...this.getSimpleGeometries(geom, type));
                });
                break;
            default:
                if (oType === type) {
                    geometries.push(geometry);
                }
        }

        return geometries.filter(geom => geom.type === (type || geom.type));
    }

    /**
     * 将数字转换为指定精度的数字
     * @param  {number} num   待转换的数字
     * @param  {number} precious 指定精度
     * @return {number}       转换后的数字
     */
    precision(num, precious = this.precious) {
        const factor = Math.pow(10, precious);
        let oNum = num * factor;
        oNum += 0.5;
        oNum = Math.floor(oNum);
        oNum /= factor;

        return oNum;
    }

    /**
     * 判断两个数值在指定精度是否相等
     * @param  {number} num1  数值1
     * @param  {number} num2  数值2
     * @param  {number} precious 精度
     * @return {boolean}      是否相等
     */
    precisionEqual(num1, num2, precious = this.precious) {
        return this.precision(num1, precious) === this.precision(num2, precious);
    }

    /**
     * 停车场矩形绘制计算两个点坐标
     * @param [x,y] fix0 屏幕坐标
     * @param [x,y] fix1 屏幕坐标
     * @param [x,y] control 屏幕坐标
     */
    restrictRect(fix0, fix1, control) {
        const p0 = new Vector(fix0.x, fix0.y);
        const p1 = new Vector(fix1.x, fix1.y);
        const c = new Vector(control.x, control.y);

        const fv = p1.minus(p0);
        const cv = c.minus(p0);
        const cvl = cv.length();

        fv.normalize();
        cv.normalize();

        const res = fv.cross(cv);
        // 垂直向量
        let perp = null;
        // 在左侧
        if (res > 0) {
            perp = new Vector(-fv.y, fv.x);
        } else if (res < 0) { // 在右侧
            perp = new Vector(fv.y, -fv.x);
        } else {
            return null;
        }

        const length = perp.dot(cv) * cvl;

        const add = perp.multiNumber(length);

        const p00 = p0.plus(add);
        const p11 = p1.plus(add);

        return [p00, p11];
    }

    getGeometriesByType(geometry, targetType) {
        if (!geometry) {
            return [];
        }
        const type = geometry.type;
        let geometries = [];
        switch (type) {
            case 'MultiPoint':
            case 'MultiLineString':
            case 'MultiPolygon':
                geometries = this.multiGeometryToArray(geometry);
                break;
            case 'GeometryCollection':
                for (let i = 0; i < geometry.geometries.length; ++i) {
                    const subGeometry = geometry.geometries[i];
                    geometries = geometries.concat(this.getGeometriesByType(subGeometry, targetType));
                }
                break;
            default:
                if (type === targetType) {
                    geometries.push(geometry);
                }
        }
        return geometries;
    }

    multiGeometryToArray(multiGeometry) {
        const geometries = [];
        const type = multiGeometry.type;
        for (let i = 0; i < multiGeometry.coordinates.length; ++i) {
            const geometry = {
                type: type.substr(5),
                coordinates: multiGeometry.coordinates[i],
            };
            geometries.push(geometry);
        }
        return geometries;
    }
}